import * as JSONC from "jsonc-parser";
import { join } from "path-browserify";
import { getEsbuild } from "@/lib/esbuild";
import { copyFiles } from "@/lib/copyFiles";
import { addDomainToCSP } from "@/lib/csp";

import { shakespearePlugin } from "./shakespearePlugin";
import { esmPlugin } from "./esmPlugin";
import { fsPlugin } from "./fsPlugin";
import { convertYarnLockToPackageLock } from "./yarnLockConverter";

import type { JSRuntimeFS } from '@/lib/JSRuntime';

export interface BuildProjectOptions {
  fs: JSRuntimeFS;
  projectPath: string;
  domParser: DOMParser;
  target?: string;
  outputPath?: string;
  esmUrl: string;
}

export interface BadgeOptions {
  enabled: boolean;
  repositoryUrl?: string;
}

async function bundle(
  options: BuildProjectOptions,
  badgeOptions?: BadgeOptions,
): Promise<Record<string, Uint8Array>> {
  const { fs, projectPath, domParser, target = "esnext", esmUrl } = options;
  const esbuild = await getEsbuild(esmUrl);

  const indexHtmlText = await fs.readFile(
    `${projectPath}/index.html`,
    "utf8",
  );

  // Read package.json
  let packageJson;
  try {
    const packageJsonText = await fs.readFile(
      `${projectPath}/package.json`,
      "utf8",
    );
    packageJson = JSON.parse(packageJsonText);
  } catch {
    packageJson = {};
  }

  // Try to read tsconfig.json
  let tsconfig;
  try {
    const tsconfigText = await fs.readFile(
      `${projectPath}/tsconfig.json`,
      "utf8",
    );
    tsconfig = JSONC.parse(tsconfigText);
  } catch {
    // tsconfig.json is optional
    tsconfig = undefined;
  }

  // Try to read package-lock.json first, fall back to yarn.lock
  let packageLock;
  let buildMode: string;
  try {
    const packageLockText = await fs.readFile(
      `${projectPath}/package-lock.json`,
      "utf8",
    );
    packageLock = JSON.parse(packageLockText);
    buildMode = "npm (package-lock.json)";
  } catch {
    // If package-lock.json doesn't exist, try yarn.lock
    try {
      const yarnLockText = await fs.readFile(
        `${projectPath}/yarn.lock`,
        "utf8",
      );
      packageLock = convertYarnLockToPackageLock(yarnLockText);
      buildMode = "yarn (yarn.lock)";
    } catch {
      // If neither exists, use empty packages object
      packageLock = { packages: {} };
      buildMode = "package.json only (no lock file)";
    }
  }

  console.log(`Building with ${buildMode}`);

  const doc = domParser.parseFromString(indexHtmlText, "text/html");
  const entryPoints: string[] = [];
  const entryScripts: Record<string, HTMLScriptElement> = {};

  // Find all module scripts with relative paths in index.html
  for (const script of doc.scripts) {
    const scriptSrc = script.getAttribute("src");

    if (scriptSrc && script.type === "module" && /^[./]/.test(scriptSrc)) {
      const path = join(projectPath, scriptSrc);

      // Files in the public directory take precedence over source files
      if (scriptSrc.startsWith("/")) {
        if (await fileExists(fs, join(projectPath, "public", scriptSrc))) {
          continue;
        }
      }

      entryPoints.push(path);
      entryScripts[path] = script;
    }
  }

  // Enable Tailwind CSS if a config file is present
  for (const path of ["tailwind.config.ts", "tailwind.config.js"]) {
    if (await fileExists(fs, join(projectPath, path))) {
      entryPoints.push(`shakespeare:${path}`);
      break;
    }
  }

  const results = await esbuild.build({
    entryPoints,
    bundle: true,
    write: false,
    format: "esm",
    target,
    outdir: "/",
    jsx: "automatic",
    metafile: true,
    sourcemap: true,
    entryNames: "[name]-[hash]",
    chunkNames: "[name]-[hash]",
    assetNames: "[name]-[hash]",
    plugins: [
      shakespearePlugin({ esmUrl }),
      fsPlugin({ fs, cwd: projectPath, tsconfig }),
      esmPlugin({ packageJson, packageLock, target, esmUrl }),
    ],
    define: {
      "import.meta.env": JSON.stringify({}),
    },
  });

  const dist: Record<string, Uint8Array> = {};

  for (const file of results.outputFiles) {
    const ext = file.path.split('.').pop();
    const filename = `${file.path.slice(1)}`;
    const entryPoint = results.metafile.outputs[filename]?.entryPoint?.replace(/^fs:/, '');
    const script = entryPoint ? entryScripts[entryPoint] : null;

    if (script) {
      script.setAttribute("src", "/" + filename);
      dist[filename] = file.contents;
    } else if (ext === "js") {
      const script = document.createElement("script");
      script.type = "module";
      script.src = "/" + filename;
      doc.head.appendChild(script);
      dist[filename] = file.contents;
    } else if (ext === "css") {
      // Embed stylesheets as Tailwind CSS styles
      const style = doc.createElement("style");
      style.setAttribute("type", "text/tailwindcss");
      style.textContent = file.text;
      doc.head.appendChild(style);
    } else {
      dist[filename] = file.contents;
    }
  }

  // Parse CSP and ensure ESM CDN is allowed for necessary directives
  const cspMeta = doc.querySelector("meta[http-equiv=\"content-security-policy\"]");
  if (cspMeta) {
    const cspContent = cspMeta.getAttribute("content");
    if (cspContent) {
      const updatedCSP = updateCSPForEsmSh(cspContent, esmUrl);
      cspMeta.setAttribute("content", updatedCSP);
    }
  }

  // Inject Shakespeare bootstrap script
  const bootstrapScript = doc.createElement("script");
  bootstrapScript.textContent = getShakespeareBootstrapScript();
  doc.head.insertBefore(bootstrapScript, doc.head.firstChild);

  // Inject AfterHours badge if enabled
  if (badgeOptions?.enabled) {
    const badgeHtml = getAfterHoursBadgeHtml(badgeOptions.repositoryUrl);
    const badgeContainer = doc.createElement("div");
    badgeContainer.innerHTML = badgeHtml;
    doc.body.appendChild(badgeContainer.firstElementChild as Element);
  }

  const updatedHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
  dist["index.html"] = new TextEncoder().encode(updatedHtml);

  return dist;
}

/**
 * Build project and write output files to the filesystem
 */
export async function buildProject(options: BuildProjectOptions): Promise<{
  files: Record<string, Uint8Array>;
  outputPath: string;
  projectId?: string;
}> {
  const { fs, projectPath, outputPath = `${projectPath}/dist` } = options;

  // Check if index.html exists
  try {
    await fs.readFile(`${projectPath}/index.html`, "utf8");
  } catch {
    throw new Error(`❌ Could not find index.html at ${projectPath}. This is required for building the project.`);
  }

  // Read badge settings from project deploy settings
  let badgeOptions: BadgeOptions | undefined;
  try {
    const deploySettingsPath = `${projectPath}/.git/shakespeare/deploy.json`;
    const deploySettingsContent = await fs.readFile(deploySettingsPath, "utf8");
    const deploySettings = JSON.parse(deploySettingsContent as string);
    if (deploySettings.showBadge) {
      badgeOptions = {
        enabled: true,
        repositoryUrl: deploySettings.repositoryUrl,
      };
    }
  } catch {
    // No deploy settings, badge not enabled
  }

  // Run the build
  const dist = await bundle(options, badgeOptions);

  // Delete all existing files in output directory
  try {
    const distFiles = await fs.readdir(outputPath);
    for (const file of distFiles) {
      await fs.unlink(`${outputPath}/${file}`);
    }
  } catch {
    // Ignore errors (e.g., directory doesn't exist)
  }

  // Create output directory if it doesn't exist
  await fs.mkdir(outputPath, { recursive: true });

  // Write all built files to output directory
  for (const [path, contents] of Object.entries(dist)) {
    await fs.writeFile(`${outputPath}/${path}`, contents);
  }

  // Copy files from public directory if it exists
  try {
    const publicPath = `${projectPath}/public`;
    const stat = await fs.stat(publicPath);
    if (stat.isDirectory()) {
      await copyFiles(fs, fs, publicPath, outputPath);
    }
  } catch {
    // Public directory doesn't exist, which is fine
  }

  // Extract project ID from the project path
  // Expected format: /projects/{projectId}
  const projectId = projectPath.split('/').pop();

  // Emit build completion event for PreviewPane to listen to
  if (projectId && typeof window !== 'undefined') {
    const buildCompleteEvent = new CustomEvent('buildComplete', {
      detail: { projectId }
    });
    window.dispatchEvent(buildCompleteEvent);
  }

  return {
    files: dist,
    outputPath,
    projectId,
  };
}

async function fileExists(fs: JSRuntimeFS, path: string): Promise<boolean> {
  try {
    const stat = await fs.stat(path);
    return stat.isFile();
  } catch {
    return false;
  }
}

/** Updates CSP to allow ESM CDN for scripts, assets, fonts, and CSS */
export function updateCSPForEsmSh(csp: string, esmUrl: string): string {
  const esmDirectives = [
    'script-src',     // For JavaScript modules
    'img-src',        // For images
    'media-src',      // For video/audio
    'font-src',       // For fonts
    'style-src',      // For CSS
    'connect-src'     // For fetch/XHR requests to ESM CDN
  ];

  return addDomainToCSP(csp, esmUrl, esmDirectives);
}

/**
 * Shakespeare Bootstrap Script
 * This script is injected into every built project's index.html to enable
 * communication with the parent Shakespeare editor.
 */
function getShakespeareBootstrapScript(): string {
  return `
(function() {
  // Only run if we're in an iframe
  if (window.parent === window) {
    return;
  }

  console.log('[Shakespeare Bootstrap] Initialized');
})();
`;
}

/**
 * AfterHours Badge HTML
 * A stylish, minimal badge that appears in the bottom-right corner of deployed sites.
 * Features a hover state that reveals a "Clone" button for easy project cloning.
 * Includes a close button that permanently hides the badge using localStorage.
 */
function getAfterHoursBadgeHtml(repositoryUrl?: string): string {
  const cloneUrl = repositoryUrl
    ? `https://afterhours.you/clone?url=${encodeURIComponent(repositoryUrl)}`
    : 'https://afterhours.you';

  return `
<div id="afterhours-badge" style="
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
">
  <style>
    #afterhours-badge-container {
      display: flex;
      align-items: center;
      gap: 0;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
      border-radius: 24px;
      padding: 6px 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;
      position: relative;
    }
    #afterhours-badge-container:hover {
      background: linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%);
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(139, 92, 246, 0.3);
    }
    #afterhours-badge-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      color: #a78bfa;
      transition: transform 0.3s ease;
    }
    #afterhours-badge-container:hover #afterhours-badge-icon {
      transform: rotate(-15deg);
    }
    #afterhours-badge-text {
      color: #e2e8f0;
      font-size: 11px;
      font-weight: 500;
      white-space: nowrap;
      margin-left: 6px;
      opacity: 1;
    }
    #afterhours-badge-clone {
      display: flex;
      align-items: center;
      gap: 4px;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
      color: white;
      font-size: 10px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 12px;
      margin-left: 0;
      width: 0;
      overflow: hidden;
      opacity: 0;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      text-decoration: none;
      white-space: nowrap;
    }
    #afterhours-badge-container:hover #afterhours-badge-clone {
      opacity: 1;
      width: auto;
      margin-left: 8px;
    }
    #afterhours-badge-clone:hover {
      background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
    }
    #afterhours-badge-clone svg {
      width: 12px;
      height: 12px;
    }
    #afterhours-badge-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin-left: 8px;
      background: transparent;
      border: none;
      color: #94a3b8;
      cursor: pointer;
      padding: 0;
      transition: all 0.2s ease;
      border-radius: 50%;
    }
    #afterhours-badge-close:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #e2e8f0;
    }
    #afterhours-badge-close svg {
      width: 12px;
      height: 12px;
    }
    #afterhours-badge.hidden {
      display: none !important;
    }
  </style>
  <div id="afterhours-badge-container">
    <div style="display: flex; align-items: center; flex: 1;" onclick="window.open('https://afterhours.you', '_blank')">
      <svg id="afterhours-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M17 8h1a4 4 0 1 1 0 8h-1"/>
        <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/>
        <line x1="6" x2="6" y1="2" y2="4"/>
        <line x1="10" x2="10" y1="2" y2="4"/>
        <line x1="14" x2="14" y1="2" y2="4"/>
      </svg>
      <span id="afterhours-badge-text">AfterHours</span>
    </div>
    <a id="afterhours-badge-clone" href="${cloneUrl}" target="_blank" onclick="event.stopPropagation()">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/>
        <path d="M9 18c-4.51 2-5-2-7-2"/>
      </svg>
      Clone
    </a>
    <button id="afterhours-badge-close" onclick="event.stopPropagation(); (function() { localStorage.setItem('afterhours-badge-hidden', 'true'); document.getElementById('afterhours-badge').classList.add('hidden'); })();" aria-label="Hide badge">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  </div>
  <script>
    (function() {
      if (localStorage.getItem('afterhours-badge-hidden') === 'true') {
        document.getElementById('afterhours-badge').classList.add('hidden');
      }
    })();
  </script>
</div>
`;
}